import {
  Button,
  ButtonProps,
  Icon,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { useGetListingReportPdfLazyQuery } from '@client/graphql/__generated__/main-operations';
import { DownloadIcon, Share2Icon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { ModalTrigger } from '~/common/components/Modal/ModalTrigger';
import { useCreateBuyerOfferAnalysis } from '../../../BuyerOfferAnalysis/hooks/useCreateBuyerOfferAnalysis';
import { CreateOfferScorecardButton } from '../CreateOfferScorecardButton';
import { useListingPageContext } from '../ListingPageContext';
import { ShareListingReportWithClientModal } from '../ShareListingReportWithClientModal';

export const ShareWithClientButton = ({
  iconOnly = true,
}: {
  iconOnly?: boolean;
}) => {
  const { isViewOnly } = useListingPageContext();

  if (isViewOnly) {
    return null;
  }

  const button = iconOnly ? (
    <Tooltip label="Share with Client">
      <IconButton
        aria-label="Share with Client"
        icon={<Icon as={Share2Icon} boxSize={5} />}
        px={2}
        size="sm"
        variant="outline"
      />
    </Tooltip>
  ) : (
    <Button
      leftIcon={<Icon as={Share2Icon} boxSize={5} />}
      size="sm"
      variant="outline"
    >
      Share with Client
    </Button>
  );

  return (
    <ModalTrigger modal={ShareListingReportWithClientModal}>
      {button}
    </ModalTrigger>
  );
};

export const OfferScorecardButton = (
  props: Omit<ButtonProps, 'onClick' | 'isLoading'>
) => {
  const { listing, isOffMarketListing, isViewOnly } = useListingPageContext();
  const navigate = useNavigate();

  const { handleCreate, loading: isCreatingScorecard } =
    useCreateBuyerOfferAnalysis();

  if (isViewOnly || isOffMarketListing) {
    return null;
  }

  const onCreateOfferScorecard = async () => {
    if (!listing?.id) {
      return;
    }

    const boa = await handleCreate({ listingId: listing.id });
    if (!boa?.id) {
      return;
    }
    navigate(`/offer-scorecard/${boa.id}`);
  };

  return (
    <CreateOfferScorecardButton
      isLoading={isCreatingScorecard}
      onClick={() => void onCreateOfferScorecard()}
      {...props}
    />
  );
};

export const DownloadPdfButton = ({
  iconOnly = true,
}: {
  iconOnly?: boolean;
}) => {
  const { listing, propertyDetails, isViewOnly } = useListingPageContext();
  const [getListingReportPdf, { loading }] = useGetListingReportPdfLazyQuery();

  const onDownload = async () => {
    const { data } = await getListingReportPdf({
      variables: {
        input: {
          listingId: listing?.id,
          propertyDetailsId: propertyDetails?.id,
        },
      },
    });

    const url = data?.getListingReportPdf?.file?.url;

    if (url) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `listing-report.pdf`;
      link.target = '_blank';

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (isViewOnly) {
    return null;
  }

  const button = iconOnly ? (
    <Tooltip
      label={
        loading
          ? 'Generating PDF, this may take a few seconds...'
          : 'Download PDF'
      }
    >
      <IconButton
        aria-label="Download PDF"
        icon={<Icon as={DownloadIcon} boxSize={5} />}
        isLoading={loading}
        px={2}
        size="sm"
        variant="outline"
        onClick={() => void onDownload()}
      />
    </Tooltip>
  ) : (
    <Button
      isLoading={loading}
      leftIcon={<Icon as={DownloadIcon} boxSize={5} />}
      loadingText="Generating PDF..."
      size="sm"
      variant="outline"
      onClick={() => void onDownload()}
    >
      Download PDF
    </Button>
  );

  return button;
};
